import { useState } from 'react';
import Sidebar from '../components/Sidebar';

function Prospects() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const prospects = [
    {
      id: 1,
      name: 'Acme Corp',
      email: 'john@acme.com',
      company: 'Acme Corporation',
      message: 'Looking to implement Kubernetes infrastructure for our SaaS platform. Need help with auto-scaling and monitoring setup.',
      status: 'new',
      date: '2024-01-15',
      source: 'contact_form'
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane@techstart.io',
      company: 'TechStart.io',
      message: 'We need a data pipeline solution for processing customer analytics. Currently using AWS but considering multi-cloud.',
      status: 'contacted',
      date: '2024-01-14',
      source: 'referral'
    },
    {
      id: 3,
      name: 'Michael Chen',
      email: 'michael@growthco.com',
      company: 'GrowthCo',
      message: 'Interested in migrating our infrastructure to GCP. Need consultation on best practices and timeline.',
      status: 'qualified',
      date: '2024-01-12',
      source: 'contact_form'
    },
    {
      id: 4,
      name: 'Sarah Williams',
      email: 'sarah@innovate.ai',
      company: 'Innovate AI',
      message: 'Looking for help setting up CI/CD pipelines and deployment automation for our ML models.',
      status: 'converted',
      date: '2024-01-10',
      source: 'website'
    },
    {
      id: 5,
      name: 'David Park',
      email: 'david@startup.xyz',
      company: 'Startup XYZ',
      message: 'Need infrastructure setup for a new project. Want to understand pricing and timeline.',
      status: 'new',
      date: '2024-01-09',
      source: 'contact_form'
    },
  ];

  const filteredProspects = prospects.filter(prospect => {
    return statusFilter === 'all' || prospect.status === statusFilter;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'badge badge-primary';
      case 'contacted':
        return 'badge badge-warning';
      case 'qualified':
        return 'badge bg-purple-500/20 text-purple-400 border border-purple-500';
      case 'converted':
        return 'badge badge-success';
      default:
        return 'badge';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const updateStatus = (prospectId: number, newStatus: string) => {
    // Handle status update
    console.log(`Updating prospect ${prospectId} to status: ${newStatus}`);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <Sidebar />

      <div className="ml-64 p-8">
        {/* Header */}
        <header className="mb-8 fade-in-up">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-display mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Prospects
              </h1>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Manage and track potential customer inquiries
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-bg-surface)] border rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-primary)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {prospects.filter(p => p.status === 'new').length} new
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="card mb-6 fade-in-up delay-100">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Filter by status:</span>
            <div className="flex items-center space-x-2">
              {['all', 'new', 'contacted', 'qualified', 'converted'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === status ? 'text-white' : ''
                  }`}
                  style={
                    statusFilter === status
                      ? { backgroundColor: 'var(--color-primary)' }
                      : { backgroundColor: 'transparent', color: 'var(--color-text-secondary)' }
                  }
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Prospects Table */}
        <div className="card fade-in-up delay-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Name</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Email</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Company</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Status</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Date</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProspects.map((prospect) => (
                  <>
                    <tr
                      key={prospect.id}
                      className="border-b transition-colors hover:bg-[var(--color-bg-elevated)] cursor-pointer"
                      style={{ borderColor: 'var(--color-border)' }}
                      onClick={() => toggleRow(prospect.id)}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'var(--color-primary)' }}>
                            {prospect.name.charAt(0)}
                          </div>
                          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{prospect.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{prospect.email}</td>
                      <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{prospect.company}</td>
                      <td className="py-4 px-4">
                        <span className={`${getStatusBadgeClass(prospect.status)} text-xs`}>
                          {getStatusLabel(prospect.status)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>{prospect.date}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="p-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors"
                            onClick={() => toggleRow(prospect.id)}
                            title={expandedRow === prospect.id ? "Collapse" : "Expand"}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${expandedRow === prospect.id ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{ color: 'var(--color-text-secondary)' }}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === prospect.id && (
                      <tr className="border-b bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                        <td colSpan={6} className="py-6 px-4">
                          <div className="max-w-4xl space-y-4">
                            <div>
                              <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Message:</h4>
                              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                                {prospect.message}
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Source:</h4>
                                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{prospect.source}</p>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Submitted:</h4>
                                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{prospect.date}</p>
                              </div>
                            </div>

                            <div className="pt-4 border-t flex items-center space-x-3" style={{ borderColor: 'var(--color-border)' }}>
                              <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Update status:</span>
                              <div className="flex items-center space-x-2">
                                {['new', 'contacted', 'qualified', 'converted'].map(status => (
                                  <button
                                    key={status}
                                    onClick={() => updateStatus(prospect.id, status)}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                                      prospect.status === status ? getStatusBadgeClass(status) : 'border'
                                    }`}
                                    style={
                                      prospect.status !== status
                                        ? { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }
                                        : {}
                                    }
                                  >
                                    {getStatusLabel(status)}
                                  </button>
                                ))}
                              </div>
                              <div className="flex-1" />
                              <button className="btn-secondary text-xs">
                                <span className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Reply
                                </span>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {filteredProspects.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>No prospects found</p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Try adjusting your filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Prospects;
